import type {ReactElement} from 'react';
import React from 'react';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {
  LINE_THICKNESS_MAX,
  LINE_THICKNESS_MIN,
} from '@Components/poster-editor/components/poster-editing-side-panel/components/line-thickness-option.tsx/line-thickness-option.types';
import {ContextualMenuSliderOptions} from '@Components/contextual-menu/components/contextual-menu-slider-options';
import {useGetKeyFromLineItem} from '@Hooks/poster-editor/useGetKeyFromLineItem';
import {degreesToRadians} from '@Utils/math.util';

export function LineThicknessOption(): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemScaleY = useGetKeyFromLineItem('scaleY');
  const activeItemHeight = useGetKeyFromLineItem('height');

  if (!activeItem || activeItemScaleY === undefined || activeItemHeight === undefined) {
    throw new Error(`Item doesn't have thickness property`);
  }

  const updateThickness = (thickness: number, undoable = true): void => {
    if (thickness <= 0) {
      return;
    }
    if (activeItem.fabricObject.height === 0) {
      return;
    }
    const newScaleY = thickness / activeItem.fabricObject.height;
    const deltaHeight = thickness - activeItem.getScaledHeight();

    void activeItem.updateFromObject(
      {
        left: activeItem.getX() + (deltaHeight / 2) * Math.sin(degreesToRadians(activeItem.getAngle())),
        top: activeItem.getY() - (deltaHeight / 2) * Math.cos(degreesToRadians(activeItem.getAngle())),
        scaleY: newScaleY,
      },
      {
        undoable,
      }
    );
  };

  const onChangeThickness = (updatedValue: number): void => {
    updateThickness(updatedValue, false);
  };

  const onChangeEnded = (updatedValue: number): void => {
    updateThickness(updatedValue);
  };

  return (
    <ContextualMenuSliderOptions
      id="line-thickness-option"
      value={Math.round(activeItemHeight * activeItemScaleY)}
      label={window.i18next.t('pmwjs_thickness')}
      range={{
        min: LINE_THICKNESS_MIN,
        max: LINE_THICKNESS_MAX,
      }}
      transformDisplay={{
        min: 1,
        max: 100,
      }}
      onChange={onChangeThickness}
      onChangeEnded={onChangeEnded}
    />
  );
}
